import {
  flow,
  getRoot,
  IAnyModelType,
  Instance,
  SnapshotIn,
  SnapshotOut,
  types,
} from 'mobx-state-tree';
import { CreditNoteItemReference } from './CreditNoteItemStore';
import { DateTime } from './DateTime';
import { CommomMixin } from './Mixins';
import { RootModelType } from './Root';
import { SaleReference, VatAmountModel } from './SaleStore';
import { modelWithCRUD } from './utils/modelWithCRUD';

export enum CreditNoteReason {
  UNSATISFACTORY = 'unsatisfactory',
  UNAVAILABLE = 'unavailable',
  ORDER_CHANGE = 'order_change',
  OTHER = 'other',
}

export const CreditNote = types
  .compose(
    CommomMixin,
    types.model({
      id: types.union(types.identifier, types.identifierNumber),
      sale: types.late(() => SaleReference),
      note: types.maybeNull(types.string),
      reason: types.enumeration<CreditNoteReason>(
        Object.values(CreditNoteReason)
      ),
      invoice_number: types.maybeNull(types.number),
      is_issued: types.boolean,
      date_issued: types.maybeNull(DateTime),
      sub_total: types.number,
      total: types.number,
      currency: types.string,
      vat_amounts: types.array(types.late(() => VatAmountModel)),
      items: types.array(types.late(() => CreditNoteItemReference)),

      credit_note_pdf: types.maybeNull(types.string),
    })
  )
  .named('CreditNote');

export const CreditNoteStore = modelWithCRUD('credit-notes', CreditNote)
  .named('CreditNoteStore')
  .actions((self) => ({
    issueCreditNote: flow(function* issueCreditNote(creditNote: ICreditNote) {
      const data = yield self.env.api
        .post(`${self.apiPath}/${creditNote.id}/issue/`, {})
        .then((res) => res.data);
      self.root.saleStore.getItem(data.sale, true);
      self.itemMap.put(data);
      return self.getItem(creditNote.id);
    }),
    sendCreditNote: flow(function* sendSale(
      creditNote: ICreditNote,
      recipients: string[],
      language: string,
      contacts_cc: boolean,
      responsible_bcc: boolean
    ) {
      const data = yield self.env.api
        .post(`${self.apiPath}/${creditNote.id}/send/`, {
          recipients,
          language,
          contacts_cc: contacts_cc || false,
          responsible_bcc: responsible_bcc || false,
        })
        .then((res) => res.data);
      yield self.getItem(creditNote.id, true);
      return data;
    }),
  }));

export interface ICreditNote extends Instance<typeof CreditNote> {}
export interface ICreditNoteSnapshotIn extends SnapshotIn<typeof CreditNote> {}
export interface ICreditNoteSnapshotOut
  extends SnapshotOut<typeof CreditNote> {}

export const CreditNoteReference = types.reference(
  types.late((): IAnyModelType => CreditNote),
  {
    get(identifier, parent): any {
      return (getRoot(parent!) as RootModelType).creditNoteStore.getItemNaive(
        identifier
      );
    },
    set(value) {
      return value.id;
    },
  }
);
